import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { Box, Typography, Modal, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es'; // Para español
import { BASE_URL_API } from '../configs/urls';
import BtnAcciones from './general/BtnAcciones';
import CheckIcon from '@mui/icons-material/Check';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { CommentModals } from './modals/CommentModals';
import { myApi } from '../configs/axiosConfig';
import { showConfirm } from '../utils/swalUtils';

export default function InspectionsTable({ refresh, setRefresh }) {
  const [rows, setRows] = useState([]);
  const [violations, setViolations] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [unreadByInspection, setUnreadByInspection] = useState({});

  useEffect(() => {
    fetchInspections();
  }, [refresh]);


  const filteredRows = rows.filter((row) => {
    const inspectionDate = new Date(row.inspection_start_date);
    const afterStart = !startDate || inspectionDate >= startDate;
    const beforeEnd = !endDate || inspectionDate <= endDate;
    return afterStart && beforeEnd;
  });


  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      return dateValue.split('T')[0];
    }
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  };

  const fetchInspections = async () => {
    const res = await axios.get(`${BASE_URL_API}/inspections`);
    setRows(res.data);

    // Una vez que cargan las inspecciones, traemos las violaciones de todas
    for (const inspection of res.data) {
      fetchViolations(inspection.inspection_id);
    }
  };

  const fetchViolations = async (inspectionId) => {
    if (violations[inspectionId]) return;
    try {
      const res = await axios.get(`${BASE_URL_API}/inspections/${inspectionId}/violations`);
      setViolations(prev => ({ ...prev, [inspectionId]: res.data }));
    } catch (error) {
      console.error(`Error al obtener violaciones para inspección ${inspectionId}`, error);
      setViolations(prev => ({ ...prev, [inspectionId]: [] })); // evita que se intente otra vez
    }
  };

  const handleRowClick = (params) => {
    const inspectionId = params.row.inspection_id;
    if (expandedRow === inspectionId) {
      setExpandedRow(null);
      setModalOpen(false);
    } else {
      fetchViolations(inspectionId);
      setExpandedRow(inspectionId);
      setModalOpen(true);
    }
  };

  const columns = [
    { field: 'inspection_id', headerName: 'ID', width: 130 },
    {
      field: 'inspection_start_date',
      headerName: 'Fecha Inspección',
      width: 150,
      valueFormatter: (params) => formatDate(params)
    },
    { field: 'inspection_start_time', headerName: 'Hora Inicio', width: 130 },
    { field: 'inspection_end_time', headerName: 'Hora Fin', width: 130 },
    { field: 'inspection_location', headerName: 'Locación', width: 150 },
    { field: 'driver_name', headerName: 'Conductor', width: 180 },
    {
      field: 'vehicle_id',
      headerName: 'Unidad',
      width: 200,
      valueGetter: (value, row) => {
        const vehicleId = row.vehicle_id || '';
        const unitNumber = row.unit_number || '';
        return unitNumber ? `${vehicleId} - ${unitNumber}` : `${vehicleId} - NO FOUND`;
      }
    },
    { field: 'report_id', headerName: 'Reporte', width: 150 },
    { field: 'inspection_level_id', headerName: 'Nivel', width: 90 },
    { field: 'carrier_name', headerName: 'Carrier', width: 200 },
    {
      field: 'reviewed', headerName: 'Revisado', width: 150,
      renderCell: (params) => {
        return params.row.reviewed === 1 ? <Chip label="Revisado" color="success" /> : <Chip label="No revisado" color="warning" />;
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      type: 'actions',
      width: 150,
      getActions: (params) => {
        const inspectionId = params.row.inspection_id;
        const hasUnread = unreadByInspection[inspectionId];

        return [
          <BtnAcciones
            icon={<CheckIcon />}
            label="Revisar"
            onClick={() => handleReview(params.row)}
          />,
          <BtnAcciones
            icon={
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <AddCommentIcon />
                {hasUnread && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: 'red',
                    }}
                  />
                )}
              </Box>
            }
            label="Agregar comentario"
            onClick={() => handleOpen(params.row)}
          />
        ];
      }
    }
  ];

  const handleReview = async (inspection) => {
    const result = await showConfirm({
      title: '¿Deseas revisar esta inspección?',
      text: `Inspección ID: ${inspection.inspection_id}`,
      confirmButtonText: 'Revisar',
      cancelButtonText: 'Cancelar',
      icon: 'question',
    });

    if (result.isConfirmed) {
      try {
        const response = await myApi.put(`${BASE_URL_API}/inspections/review`, {
          inspection_id: inspection.inspection_id
        });

        if (response.data.success) {
          setRows(prev =>
            prev.map(r =>
              r.inspection_id === inspection.inspection_id
                ? { ...r, checked: 1 }
                : r
            )
          );

          setRefresh(prev => prev + 1);
        } else {
          console.error("Error al revisar la inspección", response.data.message);
        }
      } catch (error) {
        console.error("Error en la solicitud:", error);
      }
    }
  };

  const currentUser = {
    id: sessionStorage.getItem('ebt_user_fmcsa'),
    name: sessionStorage.getItem('ebt_name_fmcsa'),
    avatar: sessionStorage.getItem('ebt_name_fmcsa').charAt(0)
  };

  // CARGAMOS LOS COMENTARIOS

  const fetchComments = async (inspection_id) => {
    try {
      const response = await myApi.get(`${BASE_URL_API}/comments/${inspection_id}`, {
        params: {
          userId: currentUser.id
        }
      });

      if (response.data.success) {
        const fetchedComments = response.data.data;
        setComments(fetchedComments);

        //Identificar comentarios no leídos
        const unreadCommentIds = fetchedComments
          .filter(comment => !comment.is_read)
          .map(comment => comment.id);

        // Registrar si hay comentarios no leídos
        setUnreadByInspection(prev => ({
          ...prev,
          [inspection_id]: unreadCommentIds.length > 0
        }));

        // Llamar al endpoint de "mark as read"
        if (unreadCommentIds.length > 0) {
          await myApi.post(`${BASE_URL_API}/comments/mark-as-read`, {
            user_id: currentUser.id,
            comment_ids: unreadCommentIds
          });

          // actualizar el estado local para marcar como leídos
          setComments(prev =>
            prev.map(c =>
              unreadCommentIds.includes(c.id) ? { ...c, is_read: true } : c
            )
          );
        }
      } else {
        console.error("Error al obtener comentarios", response.data.message);
      }
    } catch (error) {
      console.error("Error al obtener comentarios", error);
    }
  };

  const handleOpen = (params) => {
    setOpenCommentModal(true)
    setSelectedComment(params)
    const inspectionId = params.inspection_id
    fetchComments(inspectionId)
  }
  const handleClose = () => setOpenCommentModal(false);

  // Agregar nuevo comentario
  const handleSendComment = async (text, selectedComment) => {
    const response = await myApi.post(`${BASE_URL_API}/comments/add`, {
      inspection_id: selectedComment.inspection_id,
      comment: text,
      create_by: currentUser.id
    });

    if (response.data.success) {
      const nuevo = {
        id: Date.now(),
        userId: currentUser.id,
        name: currentUser.name,
        text,
        avatar: currentUser.avatar,
        inspection_id: selectedComment.inspectionId,
      };
      setComments(prev => [...prev, nuevo]);
    } else {
      console.error("Error al enviar comentario", response.data.message);
    }

  };

  // Eliminar comentario
  const handleDeleteComment = async (id) => {

    const response = await myApi.put(`${BASE_URL_API}/comments/delete`, {
      comment_id: id
    });

    if (response.data.success) {
      setComments(prev => prev.filter(c => c.id !== id));
    } else {
      console.error("Error al eliminar comentario", response.data.message);
    }
  };

  // Editar comentario
  const handleEditComment = async (commentObj, newText) => {

    const response = await myApi.put(`${BASE_URL_API}/comments/edit`, {
      inspection_id: commentObj.inspectionId,
      comment_id: commentObj.id,
      comment: newText,
      update_by: commentObj.userId
    })

    if (response.data.success) {
      setComments(prev =>
        prev.map(c => (c.id === commentObj.id ? { ...c, text: newText } : c))
      );
    } else {
      console.error("Error al editar comentario", response.data.message);
    }
  };

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, textAlign: 'center' }}>
        Inspecciones Registradas
      </Typography>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 800 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
                justifyContent: 'center',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <DatePicker
                  label="Fecha desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <DatePicker
                  label="Fecha hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </Box>
          </LocalizationProvider>

          <DataGrid
            autoHeight
            rows={filteredRows.map(r => ({ id: r.inspection_id, ...r }))}
            columns={columns.map(col => ({
              ...col,
              minWidth: col.width || 100
            }))}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 25]}
            pagination
            onRowClick={handleRowClick}
            sx={{
              width: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-root': {
                borderRadius: 2,
              },
              '& .MuiDataGrid-cell': {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
              '& .row-with-violations': {
                backgroundColor: '#ffebee', // rojo claro
                '&:hover': {
                  backgroundColor: '#ffcdd2',
                },
              },
              '& .row-no-violations': {
                backgroundColor: '#e8f5e9', // verde claro
                '&:hover': {
                  backgroundColor: '#c8e6c9',
                },
              },
              // Respeta el color de fondo personalizado aunque esté seleccionada
              '& .MuiDataGrid-row.Mui-selected.row-no-violations': {
                backgroundColor: '#e8f5e9 !important',
              },
              '& .MuiDataGrid-row.Mui-selected.row-with-violations': {
                backgroundColor: '#ffebee !important',
              },

              // Y también cuando se pasa el mouse encima de una fila seleccionada
              '& .MuiDataGrid-row.Mui-selected.row-no-violations:hover': {
                backgroundColor: '#c8e6c9 !important',
              },
              '& .MuiDataGrid-row.Mui-selected.row-with-violations:hover': {
                backgroundColor: '#ffcdd2 !important',
              },
            }}
            getRowClassName={(params) => {
              const inspectionId = params.row.inspection_id;
              const hasViolations = violations[inspectionId]?.length > 0;
              return hasViolations ? 'row-with-violations' : 'row-no-violations';
            }}
          />
        </Box>
      </Box>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          maxHeight: '80vh',
          overflowY: 'auto',
        }}>
          {/* Encabezado */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            borderBottom: '1px solid #ddd',
            pb: 1
          }}>
            <Typography variant="h6" fontWeight="bold">
              Violaciones de la inspección #{expandedRow}
            </Typography>
            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido de violaciones */}
          {violations[expandedRow]?.length ? (
            <Box component="ul" sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              {violations[expandedRow].map((v, idx) => (
                <Box
                  key={idx}
                  component="li"
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#f9f9f9',
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Código: {v.fed_vio_code}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    Categoría: {v.violation_category}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#777' }}>
                    Descripción: {v.section_desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No hay violaciones registradas.
            </Typography>
          )}
        </Box>
      </Modal>

      <CommentModals
        open={openCommentModal}
        handleClose={handleClose}
        currentUser={currentUser}
        comentarios={comments}
        onSendComment={(text) => handleSendComment(text, selectedComment)}
        onDeleteComment={handleDeleteComment}
        onEditComment={handleEditComment}
      />
    </Box>
  );
}